import React, { useState } from 'react';
import {
  Card,
  Row,
  Col,
  Tabs,
  Typography,
  Button,
  Input,
  Select,
  Alert,
  Tag,
  Collapse,
  Space,
  Divider,
  Badge,
  Anchor,
  List,
  message
} from 'antd';
import {
  ApiOutlined,
  CodeOutlined,
  CopyOutlined,
  CheckCircleOutlined,
  WarningOutlined,
  InfoCircleOutlined,
  BookOutlined,
  ThunderboltOutlined,
  SafetyOutlined,
  GlobalOutlined,
  QrcodeOutlined,
  CreditCardOutlined,
  WalletOutlined,
  BankOutlined,
  MobileOutlined,
  LinkOutlined,
  InfoOutlined
} from '@ant-design/icons';

const { Title, Paragraph, Text, Heading } = Typography;
const { TabPane } = Tabs;
const { Panel } = Collapse;
const { Link } = Anchor;

const APIDocumentation = () => {
  const [selectedMethod, setSelectedMethod] = useState('qris');
  const [copiedCode, setCopiedCode] = useState('');

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    setCopiedCode(text);
    message.success('Code copied to clipboard!');
    setTimeout(() => setCopiedCode(''), 2000);
  };

  const CodeBlock = ({ code, language = 'javascript' }) => (
    <div style={{ position: 'relative' }}>
      <Button
        type="text"
        icon={<CopyOutlined />}
        size="small"
        style={{
          position: 'absolute',
          right: 8,
          top: 8,
          zIndex: 1
        }}
        onClick={() => copyToClipboard(code)}
      >
        {copiedCode === code ? 'Copied!' : 'Copy'}
      </Button>
      <pre
        style={{
          background: '#f6f8fa',
          padding: '16px',
          borderRadius: '8px',
          overflow: 'auto',
          margin: 0,
          fontSize: '13px',
          lineHeight: '1.5',
          border: '1px solid #e1e4e8'
        }}
      >
        <code>{code}</code>
      </pre>
    </div>
  );

  const PaymentMethodsGrid = () => {
    const methods = [
      {
        code: 'QRIS',
        name: 'QRIS Payment',
        description: 'Universal QR code payment system',
        icon: <QrcodeOutlined />,
        color: '#722ed1',
        features: ['ShopeePay QRIS', 'LinkAja QRIS', 'NOBU QRIS'],
        fees: '0.7% - 1.5%',
        minAmount: 1000,
        maxAmount: 10000000
      },
      {
        code: 'VA',
        name: 'Virtual Account',
        description: 'Bank transfer virtual accounts',
        icon: <BankOutlined />,
        color: '#1890ff',
        features: ['BCA VA', 'BNI VA', 'BRI VA', 'Mandiri VA', 'CIMB Niaga VA'],
        fees: 'Rp 2.500 - Rp 5.000',
        minAmount: 10000,
        maxAmount: 100000000
      },
      {
        code: 'EWALLET',
        name: 'E-Wallet',
        description: 'Digital wallet payments',
        icon: <WalletOutlined />,
        color: '#52c41a',
        features: ['GoPay', 'OVO', 'DANA', 'ShopeePay'],
        fees: '1.5% - 2.0%',
        minAmount: 1000,
        maxAmount: 2000000
      },
      {
        code: 'CC',
        name: 'Credit Card',
        description: 'Credit and debit cards',
        icon: <CreditCardOutlined />,
        color: '#fa541c',
        features: ['Visa', 'Mastercard', 'JCB'],
        fees: '2.5% - 3.0%',
        minAmount: 1000,
        maxAmount: 50000000
      },
      {
        code: 'RETAIL',
        name: 'Retail Payment',
        description: 'Over-the-counter payments',
        icon: <MobileOutlined />,
        color: '#eb2f96',
        features: ['Alfamart', 'Indomaret'],
        fees: 'Rp 2.000 - Rp 5.000',
        minAmount: 10000,
        maxAmount: 5000000
      },
      {
        code: 'OTHER',
        name: 'Other Methods',
        description: 'Alternative payment methods',
        icon: <GlobalOutlined />,
        color: '#722ed1',
        features: ['ATM Bersama', 'Bank Permata', 'BCA VA'],
        fees: 'Variable',
        minAmount: 1000,
        maxAmount: 100000000
      }
    ];

    return (
      <Row gutter={[16, 16]}>
        {methods.map((method) => (
          <Col xs={24} sm={12} lg={8} key={method.code} style={{ paddingRight: '12px' }}>
            <Card
              hoverable
              style={{
                height: '100%',
                borderTop: `4px solid ${method.color}`
              }}
              bodyStyle={{ padding: '16px', marginRight: '8px' }}
            >
              <div style={{ textAlign: 'center', marginBottom: '16px' }}>
                <div
                  style={{
                    fontSize: '32px',
                    color: method.color,
                    marginBottom: '8px'
                  }}
                >
                  {method.icon}
                </div>
                <Title level={5} style={{ margin: 0, color: method.color }}>
                  {method.name}
                </Title>
                <Text type="secondary" style={{ fontSize: '12px' }}>
                  {method.description}
                </Text>
              </div>

              <Space direction="vertical" style={{ width: '100%' }} size="small">
                <div>
                  <Text strong>Features:</Text>
                  <div style={{ marginTop: '4px' }}>
                    {method.features.map((feature, idx) => (
                      <Tag key={idx} size="small" style={{ margin: '2px' }}>
                        {feature}
                      </Tag>
                    ))}
                  </div>
                </div>

                <div>
                  <Text strong>Fee: </Text>
                  <Text>{method.fees}</Text>
                </div>

                <div>
                  <Text strong>Min/Max: </Text>
                  <Text>
                    Rp {method.minAmount.toLocaleString()} - Rp {method.maxAmount.toLocaleString()}
                  </Text>
                </div>
              </Space>
            </Card>
          </Col>
        ))}
      </Row>
    );
  };

  const QuickStartGuide = () => (
    <div>
      <Alert
        message="Quick Start Guide"
        description="Get your Arendo integrated in 5 minutes"
        type="info"
        showIcon
        style={{ marginBottom: '24px' }}
      />

      <Row gutter={[24, 24]}>
        <Col xs={24} lg={12}>
          <Card title={<><ThunderboltOutlined /> Step 1: Get API Credentials</>}>
            <Paragraph>
              Sign up for an account and get your API keys from the dashboard.
            </Paragraph>
            <CodeBlock
              code={`{
  "merchant_id": "MERCHANT123",
  "api_key": "sk_test_123456789",
  "environment": "sandbox"
}`}
            />
          </Card>
        </Col>

        <Col xs={24} lg={12}>
          <Card title={<><CodeOutlined /> Step 2: Make Your First Payment</>}>
            <Paragraph>
              Create a payment transaction using our simple API.
            </Paragraph>
            <CodeBlock
              code={`curl -X POST https://api.arendo.co.id/v1/payments \\
  -H "Authorization: Bearer YOUR_API_KEY" \\
  -H "Content-Type: application/json" \\
  -d '{
    "amount": 10000,
    "payment_method": "QRIS",
    "description": "Test Payment",
    "customer": {
      "name": "John Doe",
      "email": "john@example.com"
    }
  }'`}
            />
          </Card>
        </Col>
      </Row>

      <Card title={<><CheckCircleOutlined /> Step 3: Handle Payment Response</>}>
        <Paragraph>
          You'll receive a payment response with QR code or payment URL.
        </Paragraph>
        <CodeBlock
          code={`{
  "success": true,
  "transaction_id": "TXN123456789",
  "payment_url": "https://payment.example.com/XYZ123",
  "qr_code": "00020101021226610016...",
  "status": "pending",
  "expires_at": "2024-01-01T12:00:00Z"
}`}
        />
      </Card>
    </div>
  );

  const APIDocumentationSection = () => (
    <div>
      <Title level={2}>API Reference</Title>
      <Paragraph>
        Complete API documentation for Arendo integration.
      </Paragraph>

      <Collapse>
        <Panel header="Create Payment" key="payment">
          <div style={{ marginBottom: '16px' }}>
            <Tag color="green">POST</Tag>
            <Text code>/v1/payments</Text>
          </div>

          <Title level={4}>Request Body</Title>
          <CodeBlock
            code={`{
  "amount": 10000,                    // Required: Amount in IDR
  "payment_method": "QRIS",          // Required: Payment method code
  "description": "Order #123",       // Required: Payment description
  "customer": {                      // Required: Customer info
    "name": "John Doe",
    "email": "john@example.com",
    "phone": "+62812345678"
  },
  "items": [                          // Optional: Order items
    {
      "name": "Product A",
      "price": 8000,
      "quantity": 1
    }
  ],
  "callback_url": "https://...",     // Optional: Webhook URL
  "return_url": "https://...",        // Optional: Return URL
  "expires_in": 3600                 // Optional: Expiration in seconds
}`}
          />

          <Title level={4}>Response</Title>
          <CodeBlock
            code={`{
  "success": true,
  "data": {
    "transaction_id": "TXN123456789",
    "payment_url": "https://payment.example.com/XYZ123",
    "qr_code": "00020101021226610016...",
    "status": "pending",
    "amount": 10000,
    "fee": 150,
    "expires_at": "2024-01-01T12:00:00Z",
    "created_at": "2024-01-01T11:00:00Z"
  }
}`}
          />
        </Panel>

        <Panel header="Get Payment Status" key="status">
          <div style={{ marginBottom: '16px' }}>
            <Tag color="blue">GET</Tag>
            <Text code>/v1/payments/{'{transaction_id}'}</Text>
          </div>

          <CodeBlock
            code={`curl https://api.arendo.co.id/v1/payments/TXN123456789 \\
  -H "Authorization: Bearer YOUR_API_KEY"`}
          />

          <Title level={4}>Response</Title>
          <CodeBlock
            code={`{
  "success": true,
  "data": {
    "transaction_id": "TXN123456789",
    "status": "success",              // pending, success, failed, expired
    "amount": 10000,
    "fee": 150,
    "paid_at": "2024-01-01T11:05:00Z",
    "payment_method": "QRIS",
    "customer": {
      "name": "John Doe",
      "email": "john@example.com"
    }
  }
}`}
          />
        </Panel>

        <Panel header="Get Payment Methods" key="methods">
          <div style={{ marginBottom: '16px' }}>
            <Tag color="blue">GET</Tag>
            <Text code>/v1/payment-methods?amount=10000</Text>
          </div>

          <CodeBlock
            code={`curl https://api.arendo.co.id/v1/payment-methods?amount=10000 \\
  -H "Authorization: Bearer YOUR_API_KEY"`}
          />

          <Title level={4}>Response</Title>
          <CodeBlock
            code={`{
  "success": true,
  "data": [
    {
      "code": "QRIS",
      "name": "QRIS Payment",
      "icon": "https://.../qris.png",
      "fee": {
        "flat": 0,
        "percent": 0.7
      },
      "available": true,
      "min_amount": 1000,
      "max_amount": 10000000
    }
  ]
}`}
          />
        </Panel>

        <Panel header="Webhook/Callback" key="webhook">
          <Title level={4}>Payment Callback Format</Title>
          <CodeBlock
            code={`{
  "event": "payment.success",
  "transaction_id": "TXN123456789",
  "status": "success",
  "amount": 10000,
  "fee": 150,
  "payment_method": "QRIS",
  "paid_at": "2024-01-01T11:05:00Z",
  "signature": "abc123...",          // Verify this signature
  "customer": {
    "name": "John Doe",
    "email": "john@example.com"
  }
}`}
          />

          <Alert
            message="Security Note"
            description="Always verify webhook signatures using your API key: MD5(transaction_id + amount + api_key)"
            type="warning"
            showIcon
            style={{ marginTop: '16px' }}
          />
        </Panel>
      </Collapse>
    </div>
  );

  const CodeExamples = () => (
    <div>
      <Title level={2}>Code Examples</Title>
      <Paragraph>
        Ready-to-use code samples in popular programming languages.
      </Paragraph>

      <Tabs defaultActiveKey="javascript">
        <TabPane tab="JavaScript" key="javascript">
          <Card title="JavaScript/Node.js">
            <CodeBlock
              code={`// Install our SDK
npm install arendo-sdk

// Initialize
const PaymentGateway = require('arendo-sdk');
const pg = new PaymentGateway('sk_test_123456789');

// Create payment
async function createPayment() {
  try {
    const payment = await pg.payments.create({
      amount: 10000,
      payment_method: 'QRIS',
      description: 'Test payment',
      customer: {
        name: 'John Doe',
        email: 'john@example.com'
      }
    });

    console.log('Payment URL:', payment.payment_url);
    console.log('QR Code:', payment.qr_code);

  } catch (error) {
    console.error('Error:', error);
  }
}

createPayment();`}
            />
          </Card>
        </TabPane>

        <TabPane tab="PHP" key="php">
          <Card title="PHP">
            <CodeBlock
              code={`<?php
// Using Composer
composer require payment-gateway/sdk

use PaymentGateway\\\\PaymentGateway;

// Initialize
$pg = new PaymentGateway('sk_test_123456789');

// Create payment
try {
    $payment = $pg->payments()->create([
        'amount' => 10000,
        'payment_method' => 'QRIS',
        'description' => 'Test payment',
        'customer' => [
            'name' => 'John Doe',
            'email' => 'john@example.com'
        ]
    ]);

    echo "Payment URL: " . $payment['payment_url'] . "\\n";
    echo "QR Code: " . $payment['qr_code'] . "\\n";

} catch (Exception $e) {
    echo "Error: " . $e->getMessage() . "\\n";
}
?>`}
            />
          </Card>
        </TabPane>

        <TabPane tab="Python" key="python">
          <Card title="Python">
            <CodeBlock
              code={`# Install our SDK
pip install arendo-sdk

from payment_gateway import PaymentGateway

# Initialize
pg = PaymentGateway(api_key='sk_test_123456789')

# Create payment
try:
    payment = pg.payments.create({
        'amount': 10000,
        'payment_method': 'QRIS',
        'description': 'Test payment',
        'customer': {
            'name': 'John Doe',
            'email': 'john@example.com'
        }
    })

    print(f"Payment URL: {payment['payment_url']}")
    print(f"QR Code: {payment['qr_code']}")

except Exception as e:
    print(f"Error: {e}")`}
            />
          </Card>
        </TabPane>

        <TabPane tab="React" key="react">
          <Card title="React.js">
            <CodeBlock
              code={`import React, { useState } from 'react';
import PaymentGatewaySDK from 'arendo-sdk';

const PaymentForm = () => {
  const [loading, setLoading] = useState(false);
  const [payment, setPayment] = useState(null);

  const handlePayment = async () => {
    setLoading(true);

    try {
      const pg = new PaymentGatewaySDK('sk_test_123456789');

      const result = await pg.createPayment({
        amount: 10000,
        payment_method: 'QRIS',
        description: 'Test payment',
        customer: {
          name: 'John Doe',
          email: 'john@example.com'
        }
      });

      setPayment(result);

    } catch (error) {
      console.error('Payment error:', error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div>
      <button
        onClick={handlePayment}
        disabled={loading}
      >
        {loading ? 'Creating...' : 'Create Payment'}
      </button>

      {payment && (
        <div>
          <img src={payment.qr_code_url} alt="QR Code" />
          <a href={payment.payment_url}>Pay Now</a>
        </div>
      )}
    </div>
  );
};

export default PaymentForm;`}
            />
          </Card>
        </TabPane>
      </Tabs>
    </div>
  );

  const TestingGuide = () => (
    <div>
      <Title level={2}>Testing & Integration</Title>
      <Paragraph>
        Complete guide for testing your payment integration.
      </Paragraph>

      <Row gutter={[24, 24]}>
        <Col xs={24} lg={12}>
          <Card title={<><SafetyOutlined /> Sandbox Environment</>}>
            <Paragraph>
              Use our sandbox environment for testing without real money.
            </Paragraph>

            <Space direction="vertical" style={{ width: '100%' }}>
              <div>
                <Text strong>Sandbox URL:</Text>
                <Text code copyable>https://sandbox-api.arendo.co.id</Text>
              </div>

              <div>
                <Text strong>Test Credentials:</Text>
                <CodeBlock
                  code={`{
  "merchant_id": "TEST_MERCHANT",
  "api_key": "sk_test_sandbox_key"
}`}
                />
              </div>

              <Alert
                message="Test Cards"
                description={
                  <div>
                    <div>Success: 4111111111111111</div>
                    <div>Fail: 4000000000000002</div>
                  </div>
                }
                type="info"
              />
            </Space>
          </Card>
        </Col>

        <Col xs={24} lg={12}>
          <Card title={<><CheckCircleOutlined /> Testing Checklist</>}>
            <List
              dataSource={[
                '✓ Create payment with valid amount',
                '✓ Test different payment methods',
                '✓ Handle success response',
                '✓ Handle error scenarios',
                '✓ Implement webhook verification',
                '✓ Test payment status updates',
                '✓ Verify callback signatures',
                '✓ Test refund flow (if applicable)',
                '✓ Handle expired payments',
                '✓ Load testing for high volume'
              ]}
              renderItem={(item) => (
                <List.Item style={{ padding: '4px 0' }}>
                  <Text>{item}</Text>
                </List.Item>
              )}
            />
          </Card>
        </Col>
      </Row>

      <Card title={<><WarningOutlined /> Common Issues</>}>
        <Collapse>
          <Panel header="Invalid API Key" key="1">
            <Paragraph>
              Ensure you're using the correct API key for the environment (sandbox vs production).
            </Paragraph>
          </Panel>

          <Panel header="Signature Verification Failed" key="2">
            <Paragraph>
              Check webhook signature calculation: MD5(transaction_id + amount + api_key)
            </Paragraph>
          </Panel>

          <Panel header="Payment Method Not Available" key="3">
            <Paragraph>
              Some payment methods have minimum/maximum amount requirements.
            </Paragraph>
          </Panel>
        </Collapse>
      </Card>
    </div>
  );

  return (
    <div style={{ padding: '24px', paddingRight: '180px', maxWidth: '1400px', margin: '0 auto' }}>
      <div style={{ marginBottom: '32px', textAlign: 'center' }}>
        <Title level={1} style={{ marginBottom: '16px' }}>
          <ApiOutlined /> {process.env.REACT_APP_NAME || 'Arendo'} API Documentation
        </Title>
        <Paragraph style={{ fontSize: '18px', color: '#666' }}>
          Complete integration guide for merchants and developers
        </Paragraph>

        <Space size="large" style={{ marginTop: '16px' }}>
          <Badge count="Live" color="green">
            <Tag color="green">Production Ready</Tag>
          </Badge>
          <Tag color="blue">REST API</Tag>
          <Tag color="purple">JSON</Tag>
          <Tag color="orange">Webhooks</Tag>
        </Space>
      </div>

      <Anchor style={{ position: 'fixed', top: '100px', right: '40px' }}>
        <Link href="#payment-methods" title="Payment Methods" />
        <Link href="#quick-start" title="Quick Start" />
        <Link href="#api-reference" title="API Reference" />
        <Link href="#code-examples" title="Code Examples" />
        <Link href="#testing" title="Testing" />
      </Anchor>

      <div id="payment-methods">
        <Title level={2}>Payment Methods</Title>
        <PaymentMethodsGrid />
      </div>

      <Divider style={{ margin: '48px 0' }} />

      <div id="quick-start">
        <QuickStartGuide />
      </div>

      <Divider style={{ margin: '48px 0' }} />

      <div id="api-reference">
        <APIDocumentationSection />
      </div>

      <Divider style={{ margin: '48px 0' }} />

      <div id="code-examples">
        <CodeExamples />
      </div>

      <Divider style={{ margin: '48px 0' }} />

      <div id="testing">
        <TestingGuide />
      </div>

      <Divider style={{ margin: '48px 0' }} />

      <Card>
        <div style={{ textAlign: 'center' }}>
          <Title level={3}>Need Help?</Title>
          <Paragraph>
            Contact our support team for assistance with integration.
          </Paragraph>
          <Space>
            <Button type="primary" icon={<BookOutlined />}>
              View Full Documentation
            </Button>
            <Button icon={<LinkOutlined />}>
              API Status
            </Button>
            <Button icon={<InfoOutlined />}>
              Support Center
            </Button>
          </Space>
        </div>
      </Card>
    </div>
  );
};

export default APIDocumentation;